import { filter, take, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '@auth/services/auth/auth.service';
import { Device } from '@capacitor/device';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { SwitchControlComponent } from '@shared/components/forms/switch-control/switch-control.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ToastTypes } from '@shared/models/toast';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { INotificationSettings, INotificationSettingsForm, IUser } from '@user/models/user';
import { IUserDevice } from '@user/models/user-device';
import { UserDeviceService } from '@user/services/user-device/user-device.service';
import { UsersService } from '@user/services/users/users.service';

import { UserDeviceCardComponent } from '../user-device-card/user-device-card.component';

@Component({
	selector: 'app-notifications',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		LoadingStateComponent,
		AlertComponent,
		SwitchControlComponent,
		UserDeviceCardComponent,
	],
	templateUrl: './notifications.component.html',
	styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit, OnDestroy {
	notificationForm: FormGroup<INotificationSettingsForm>;
	destroyed$ = new EventEmitter<void>();

	isLoading = true;
	isSaving = false;

	currentUser: IUser;
	currentDeviceId: string;
	userDevices: Array<IUserDevice> = [];

	private usersService = inject(UsersService);
	private userDeviceService = inject(UserDeviceService);
	private toastService = inject(ToastService);
	private authService = inject(AuthService);
	private modalService = inject(ModalService);

	ngOnInit(): void {
		this.notificationForm = new FormGroup<INotificationSettingsForm>({
			notificationsEnabled: new FormControl<boolean | null>(false, [Validators.required]),
			newLeads: new FormControl<boolean | null>(false, [Validators.required]),
			assessmentStatus: new FormControl<boolean | null>(false, [Validators.required]),
			assignedLeadAssessment: new FormControl<boolean | null>(false, [Validators.required]),
		});

		this.subscribeToCurrentUser();
		this.subscribeToCurrentDevice();
		this.subscribeToDevices();
		this.subscribeToFormChanges();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	subscribeToCurrentUser(): void {
		if (!this.authService.currentUser) {
			this.isLoading = false;
			return;
		}
		this.usersService
			.getDocumentData(this.authService.currentUser.uid)
			.pipe(
				takeUntil(this.destroyed$),
				filter((user) => !!user),
			)
			.subscribe((user) => {
				this.currentUser = user;
				this.isLoading = false;

				this.resetForm();
			});
	}

	subscribeToDevices(): void {
		if (!this.authService.currentUser) {
			return;
		}
		this.userDeviceService
			.listDevices(this.authService.currentUser.uid)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((devices) => {
				this.userDevices = devices;
			});
	}

	subscribeToFormChanges(): void {
		this.notificationForm.controls.notificationsEnabled.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			if (value) {
				this.notificationForm.controls.newLeads.enable();
				this.notificationForm.controls.assessmentStatus.enable();
				this.notificationForm.controls.assignedLeadAssessment.enable();
				this.notificationForm.patchValue({
					newLeads: this.currentUser?.settings?.notifications.newLeads || false,
					assessmentStatus: this.currentUser?.settings?.notifications.assessmentStatus || false,
					assignedLeadAssessment: this.currentUser?.settings?.notifications.assignedLeadAssessment || false,
				});
			} else {
				this.notificationForm.controls.newLeads.disable();
				this.notificationForm.controls.assessmentStatus.disable();
				this.notificationForm.controls.assignedLeadAssessment.disable();

				this.notificationForm.patchValue({
					newLeads: false,
					assessmentStatus: false,
					assignedLeadAssessment: false,
				});
			}
		});
	}

	subscribeToCurrentDevice(): void {
		Device.getId().then((device) => {
			this.currentDeviceId = device.identifier;
		});
	}

	resetForm(): void {
		this.notificationForm.patchValue({
			notificationsEnabled: this.currentUser?.settings?.notifications?.notificationsEnabled || false,
			newLeads: this.currentUser?.settings?.notifications.newLeads || false,
			assessmentStatus: this.currentUser?.settings?.notifications.assessmentStatus || false,
			assignedLeadAssessment: this.currentUser?.settings?.notifications.assignedLeadAssessment || false,
		});
	}

	handleCancel(): void {
		this.resetForm();
		this.notificationForm.markAsPristine();
		this.notificationForm.markAsUntouched();
	}

	handleSave(): void {
		if (this.notificationForm.invalid) {
			return;
		}

		this.isSaving = true;

		this.usersService
			.updateUserNotificationSettings(this.currentUser.uid, this.notificationForm.getRawValue() as INotificationSettings)
			.then(() => {
				this.isSaving = false;
				this.notificationForm.markAsPristine();

				this.toastService.showToast({
					title: 'Notifications',
					message: 'Successfully updated notification settings',
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error(error);
				this.isSaving = false;

				this.toastService.showToast({
					title: 'Notifications',
					message: `Failed to update notifications: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	handleRemoveDevice(device: IUserDevice): void {
		const modalRef = this.modalService.open(ConfirmationModalComponent, false, 'md');
		const componentInstance = modalRef.componentInstance as ConfirmationModalComponent;
		componentInstance.title = 'Remove Device';
		componentInstance.contentText = `Are you sure you want to remove this device? Once removed, you will no longer receive notifications on this device and you will need to re-enable notifications.`;
		componentInstance.confirmAction.pipe(take(1)).subscribe(() => {
			this.userDeviceService
				.removeDevice(this.currentUser.uid, device.id!)
				.then(() => {
					this.toastService.showToast({
						title: 'Notifications',
						message: 'Successfully removed device',
						type: ToastTypes.SUCCESS,
					});
				})
				.catch((error) => {
					console.error(error);
					this.toastService.showToast({
						title: 'Notifications',
						message: `Failed to remove device: ${error.message}`,
						type: ToastTypes.ERROR,
					});
				});
		});
	}
}
